import { useState, useMemo, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import type { TFunction } from 'i18next';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';

import { Card, CardContent } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { FullscreenFormDialog } from '@/components/ui/fullscreen-form-dialog';
import { Upload, X, FileIcon, Plus } from 'lucide-react';

import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useProjects } from '@/hooks/useProjects';
import { useTranslation } from '@/hooks/useTranslation';
import { useOrgNavigate } from '@/hooks/useOrgNavigate';
import {
  useCreateSustainabilityActivity,
  useUpdateSustainabilityActivity,
  type SustainabilityActivity,
} from '@/hooks/useSustainabilityActivities';
import { useUploadActivityAttachments } from '@/hooks/useActivityAttachments';
import {
  CarbonImpactPicker,
  type CarbonImpactValue,
  type CarbonImpactResolved,
} from '@/components/carbon/CarbonImpactPicker';

const buildFormSchema = (t: TFunction) =>
  z.object({
    activity_date: z.date({ required_error: t('form.validation.activityDateRequired') }),
    activity_description: z.string().min(1, t('form.validation.descriptionRequired')),
    project_id: z.string().optional(),
    location_id: z.string().optional(),
  });

type FormSchema = ReturnType<typeof buildFormSchema>;

interface CreateSustainabilityActivityDialogProps {
  trigger?: React.ReactNode;
  activity?: SustainabilityActivity | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onActivityCreated?: () => void;
  /** Navigate to the Sustainability activities page after a successful CREATE.
   *  Default false (the page button and grid-edit stay put); quick-add passes true. */
  navigateOnSuccess?: boolean;
}

/**
 * Thin entry point: trigger button (uncontrolled mode) + open state. Conditionally
 * mounts the self-contained fullscreen form so its data hooks (projects, emission
 * tables, locations, carbon config) only fire once the dialog opens.
 */
export function CreateSustainabilityActivityDialog({
  trigger,
  activity,
  open: controlledOpen,
  onOpenChange,
  onActivityCreated,
  navigateOnSuccess = false,
}: CreateSustainabilityActivityDialogProps) {
  const { t } = useTranslation('sustainability');
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const isEditing = !!activity;

  return (
    <>
      {!isEditing && !isControlled && (
        <div onClick={() => setOpen(true)}>
          {trigger || (
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              {t('logActivity')}
            </Button>
          )}
        </div>
      )}

      {open && (
        <ActivityFormDialog
          activity={activity}
          onOpenChange={setOpen}
          onActivityCreated={onActivityCreated}
          navigateOnSuccess={navigateOnSuccess}
        />
      )}
    </>
  );
}

interface ActivityFormDialogProps {
  activity?: SustainabilityActivity | null;
  /** Called with `false` to request close — the wrapper unmounts this component. */
  onOpenChange: (open: boolean) => void;
  onActivityCreated?: () => void;
  navigateOnSuccess?: boolean;
}

/**
 * The fullscreen "Activity" document form (document-metaphor per
 * docs/FULLSCREEN_FORM_MODALS.md). Self-contained: owns the form, attachments, and
 * the create/update mutations. The carbon factor-finder + quantity + live estimate
 * is the shared `CarbonImpactPicker` (also used by the expense/AR line items).
 */
function ActivityFormDialog({
  activity,
  onOpenChange,
  onActivityCreated,
  navigateOnSuccess = false,
}: ActivityFormDialogProps) {
  const { t } = useTranslation('sustainability');
  const { t: tCommon } = useTranslation('common');
  const { selectedOrganization } = useOrganization();
  const { data: projects } = useProjects(selectedOrganization?.id);
  const orgNavigate = useOrgNavigate();
  const isEditing = !!activity;
  const initialData = activity;

  const createActivity = useCreateSustainabilityActivity();
  const updateActivity = useUpdateSustainabilityActivity();
  const uploadAttachments = useUploadActivityAttachments();

  const [attachments, setAttachments] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Carbon impact (table + dimensions + quantity + unit) lives outside RHF — the
  // shared CarbonImpactPicker owns its rendering + effects; we hold the value and
  // validate it on submit.
  const initialCarbon = useMemo<CarbonImpactValue>(
    () => ({
      emissionTableId: initialData?.emission_table_id || '',
      dimensionValues: initialData?.dimension_values || {},
      quantity: initialData?.quantity || 0,
      quantityUnit: initialData?.quantity_unit || '',
    }),
    [initialData],
  );
  const [carbon, setCarbon] = useState<CarbonImpactValue>(initialCarbon);
  const [carbonDirty, setCarbonDirty] = useState(false);
  const [carbonErrors, setCarbonErrors] = useState<
    Partial<Record<'emissionTableId' | 'quantity' | 'quantityUnit', string>>
  >({});
  // The resolved factor's input unit — needed to normalize the quantity for the RPC.
  const factorInputUnitRef = useRef<string | null>(null);

  const handleCarbonChange = useCallback(
    (next: CarbonImpactValue) => {
      setCarbon(next);
      setCarbonDirty(JSON.stringify(next) !== JSON.stringify(initialCarbon));
    },
    [initialCarbon],
  );
  const handleResolved = useCallback((r: CarbonImpactResolved) => {
    factorInputUnitRef.current = r.factorInputUnitCode;
  }, []);

  // Fetch locations
  const { data: locations } = useQuery({
    queryKey: ['gis-locations', selectedOrganization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gis_locations')
        .select('id, formatted_address')
        .eq('organization_id', selectedOrganization?.id)
        .order('formatted_address');
      if (error) throw error;
      return data;
    },
    enabled: !!selectedOrganization?.id,
  });

  const formSchema = useMemo(() => buildFormSchema(t), [t]);

  const form = useForm<z.infer<FormSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      activity_date: initialData?.activity_date ? new Date(initialData.activity_date) : new Date(),
      activity_description: initialData?.activity_description || '',
      project_id: initialData?.project_id || '',
      location_id: initialData?.location_id || '',
    },
  });

  const validateCarbon = () => {
    const errs: Partial<Record<'emissionTableId' | 'quantity' | 'quantityUnit', string>> = {};
    if (!carbon.emissionTableId) errs.emissionTableId = t('form.validation.emissionTableRequired');
    if (!(carbon.quantity > 0)) errs.quantity = t('form.validation.quantityPositive');
    if (!carbon.quantityUnit) errs.quantityUnit = t('form.validation.unitRequired');
    setCarbonErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (values: z.infer<FormSchema>) => {
    if (!selectedOrganization?.id) return;
    if (!validateCarbon()) return;

    setIsSubmitting(true);
    try {
      const activityInput = {
        organizationId: selectedOrganization.id,
        activityDate: format(values.activity_date, 'yyyy-MM-dd'),
        activityDescription: values.activity_description,
        quantity: carbon.quantity,
        quantityUnit: carbon.quantityUnit,
        // The dropdown writes canonical unit codes, so quantity_unit IS the code.
        quantityUnitCode: carbon.quantityUnit,
        factorInputUnitCode: factorInputUnitRef.current,
        emissionTableId: carbon.emissionTableId,
        dimensionValues: carbon.dimensionValues,
        projectId: values.project_id || null,
        locationId: values.location_id || null,
      };

      let activityId: string;

      if (isEditing && activity) {
        await updateActivity.mutateAsync({ ...activityInput, activityId: activity.id });
        activityId = activity.id;
      } else {
        const result = await createActivity.mutateAsync(activityInput);
        const createdRow = Array.isArray(result) ? result[0] : result;
        if (!createdRow?.activity_id) throw new Error('Activity was not created');
        activityId = createdRow.activity_id;
      }

      if (attachments.length > 0 && activityId) {
        await uploadAttachments.mutateAsync({
          organizationId: selectedOrganization.id,
          activityId,
          files: attachments,
        });
      }

      setAttachments([]);
      // Continue to the activities page when created from outside it (e.g.
      // quick-add). Replace BEFORE close so the dialog wrapper sees its history
      // entry is gone and skips its own history.back(). Edits close in place.
      if (!isEditing && navigateOnSuccess) {
        orgNavigate('/impact', { replace: true });
      }
      onOpenChange(false);
      onActivityCreated?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setAttachments([...attachments, ...files]);
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  return (
    <FullscreenFormDialog
      open
      onOpenChange={onOpenChange}
      title={isEditing ? t('editActivity') : t('logActivityTitle')}
      onSubmit={form.handleSubmit(onSubmit)}
      submitLabel={isEditing ? tCommon('saveChanges') : t('logActivity')}
      isSubmitting={isSubmitting}
      formId="sustainability-activity-form"
      skipCloseConfirmation={!form.formState.isDirty && attachments.length === 0 && !carbonDirty}
    >
      <Form {...form}>
        <div className="space-y-8">
          {/* Links row — internal associations above the document */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="project_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.projectOptional')}</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(value === '__none__' ? '' : value)}
                    value={field.value || '__none__'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('form.selectProject')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="__none__">{t('form.none')}</SelectItem>
                      {projects?.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="location_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.locationOptional')}</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(value === '__none__' ? '' : value)}
                    value={field.value || '__none__'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('form.selectLocation')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="__none__">{t('form.none')}</SelectItem>
                      {locations?.map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.formatted_address}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>

          {/* Document card */}
          <Card>
            <CardContent className="pt-6 space-y-8">
              <h2 className="text-[40px] font-extralight leading-tight text-right mb-3 uppercase">
                {t('form.heroTitle')}
              </h2>

              {/* Activity details */}
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  {t('form.activityDetails')}
                </h3>
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="activity_date"
                    render={({ field, fieldState }) => (
                      <FormItem className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <FormLabel className="font-extralight">{t('form.activityDate')}</FormLabel>
                          {fieldState.error && (
                            <span className="text-xs text-destructive">{fieldState.error.message}</span>
                          )}
                        </div>
                        <FormControl>
                          <DatePicker
                            value={field.value}
                            onChange={field.onChange}
                            className={fieldState.error ? 'border-destructive' : ''}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="activity_description"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormLabel className="font-extralight">{t('form.description')}</FormLabel>
                          {fieldState.error && (
                            <span className="text-xs text-destructive">{fieldState.error.message}</span>
                          )}
                        </div>
                        <FormControl>
                          <Textarea
                            placeholder={t('form.descriptionPlaceholder')}
                            className={fieldState.error ? 'border-destructive' : ''}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Carbon calculation — the shared factor-finder, full-bleed band */}
              <div className="-mx-6 px-6 py-6 bg-muted">
                <CarbonImpactPicker
                  value={carbon}
                  onChange={handleCarbonChange}
                  onResolvedChange={handleResolved}
                  errors={carbonErrors}
                />
              </div>

              {/* Attachments */}
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  {t('form.attachments')}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('file-upload')?.click()}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {t('form.uploadFiles')}
                    </Button>
                    <input
                      id="file-upload"
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleFileChange}
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    />
                    <p className="text-sm text-muted-foreground">{t('form.uploadHint')}</p>
                  </div>

                  {attachments.length > 0 && (
                    <div className="space-y-2">
                      {attachments.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-muted rounded-md"
                        >
                          <div className="flex items-center gap-2">
                            <FileIcon className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{file.name}</span>
                            <span className="text-xs text-muted-foreground">
                              ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAttachment(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </Form>
    </FullscreenFormDialog>
  );
}
